/**
 * Mastery — one conversion per weapon, unlocked at weapon level 40.
 *
 * A mastery is deliberately **not** a strict upgrade, and that is the whole design.
 * A part that is simply better than the base weapon turns level 40 into a wall
 * between two tiers of player rather than a fork in the road; a build that has
 * earned one should look different, not just win more. So every entry here either
 * sharpens the weapon's role until it can do nothing else, or inverts it outright —
 * and each one pays for that with something the base weapon had.
 *
 * Each mastery occupies one of the eleven ordinary slots, chosen to fit what it
 * physically is. That matters: a mastery competes for a slot and for two of the five
 * build points, so taking it costs a real part rather than being free real estate.
 *
 * A few are large enough to move a *breakpoint* — the number of rounds a kill takes.
 * Those are called out in the comments, because a breakpoint change is worth far
 * more than the percentage that produced it and is the only place in this file where
 * a 12% damage modifier is a bigger deal than a 45% recoil one.
 *
 * Requires: weapon level 40. Cost: 2 points. One per weapon, never shared.
 */

import { add, mult, scale, set, validateMod } from './schema.js';

const M = [
  /* --------------------------------- rifles --------------------------------- */
  {
    id: 'my_ak27', weapon: 'ak27', slot: 'barrel', name: 'MILLSTONE HEAVY CARRIER',
    blurb: 'A heavier carrier, a slower cycle and a chambering that hits like a door.',
    // 26 -> 33.8 damage. Four rounds becomes three, at four fifths the fire rate:
    // a faster kill on a weapon that can no longer afford to miss.
    mods: [
      mult('damage.mult', 0.30), scale('rpm', 0.80),
      mult('magSize', -0.33), mult('recoil.climb30Deg', 0.35),
      mult('ads.in', 0.18), mult('damage.range', -0.20),
    ],
    pro: 'A three-round kill, and the fastest one the rifle class has.',
    con: 'Twenty rounds, a brutal climb, and it gives up range to get there.',
  },
  {
    id: 'my_x9maverick', weapon: 'x9maverick', slot: 'fireMod', name: 'FUSILLADE SEAR',
    blurb: 'The burst cam comes out. What is left runs open until the magazine does.',
    mods: [
      set('fireMode', 'auto'), scale('rpm', 0.72),
      mult('damage.mult', -0.12), mult('recoil.climb30Deg', -0.15), mult('magSize', 0.33),
      mult('ads.in', 0.10), mult('spread.bloomGain', 0.20),
    ],
    pro: 'Forty rounds of controllable automatic fire from a burst rifle.',
    con: 'The one-burst kill is gone, and nothing replaces it.',
  },
  {
    id: 'my_ds20mirage', weapon: 'ds20mirage', slot: 'barrel', name: 'LONGLIGHT MATCH KIT',
    blurb: 'A match barrel and a free-floated shroud, cut for the far end of a street.',
    mods: [
      mult('damage.range', 0.45), mult('muzzleVelocity', 0.25), mult('penetration', 0.40),
      mult('ads.in', 0.22), mult('ergonomics.sprintOut', 0.25),
      mult('spread.hip', 0.30), mult('ergonomics.adsMoveScale', -0.15),
    ],
    pro: 'The longest-reaching assault rifle on the roster.',
    con: 'Useless inside a room and slow to bring to bear anywhere.',
  },
  {
    id: 'my_peacekeepermk1', weapon: 'peacekeepermk1', slot: 'barrel', name: 'TRENCHFIST SHORTY',
    blurb: 'Chopped to the gas block, stock folded, set up for doorways.',
    mods: [
      mult('ads.in', -0.25), mult('ergonomics.sprintOut', -0.30),
      mult('spread.hip', -0.30), mult('spread.hipMoving', -0.30),
      mult('ergonomics.adsMoveScale', 0.12),
      mult('damage.range', -0.38), mult('muzzleVelocity', -0.25), mult('recoil.climb30Deg', 0.25),
    ],
    pro: 'An assault rifle that handles like a submachine gun.',
    con: 'And it now has a submachine gun’s reach, on a rifle that was never a hard hitter.',
  },
  {
    id: 'my_m15mod0', weapon: 'm15mod0', slot: 'fireMod', name: 'MOD 1 RECEIVER',
    blurb: 'A lightened bolt and a shortened gas system. It runs far faster than issue.',
    mods: [
      scale('rpm', 1.28),
      mult('recoil.climb30Deg', 0.30), mult('spread.bloomGain', 0.60),
      mult('magSize', -0.20), mult('damage.range', -0.15), mult('recoil.random', 0.25),
    ],
    pro: 'The fastest-firing assault rifle in the game.',
    con: 'Twenty-four rounds that bloom, wander and are gone in a second and a half.',
  },
  {
    id: 'my_mxr17', weapon: 'mxr17', slot: 'fireMod', name: 'ANVILSHOT CONVERSION',
    blurb: 'Rechambered for a heavier, slower round the receiver barely tolerates.',
    // 33 -> 36.96. Four rounds becomes three; the rate drop is smaller than the
    // round saved, so the kill genuinely gets faster.
    mods: [
      mult('damage.mult', 0.12), scale('rpm', 0.82),
      mult('magSize', -0.20), mult('recoil.climb30Deg', 0.30), mult('ads.in', 0.15),
    ],
    pro: 'A three-round kill from the hardest-hitting rifle on the roster.',
    con: 'Twenty rounds, a slower cycle, and a climb that punishes the third one.',
  },

  /* ---------------------------------- SMGs ---------------------------------- */
  {
    id: 'my_razor9mm', weapon: 'razor9mm', slot: 'fireMod', name: 'WHIPCORD SPRING KIT',
    blurb: 'A racing spring and a shaved sear. It empties itself if you let it.',
    mods: [
      scale('rpm', 1.22),
      mult('recoil.climb30Deg', 0.30), mult('spread.bloomGain', 0.45),
      mult('magSize', -0.20), mult('damage.range', -0.18),
    ],
    pro: 'The highest rate of fire in the game, and the shortest time to kill with it.',
    con: 'Twenty-four rounds, a cone that opens fast, and no reach whatsoever.',
  },
  {
    id: 'my_mpc25', weapon: 'mpc25', slot: 'fireMod', name: 'SPLITSTOCK AUTO SEAR',
    blurb: 'The four-round cam removed and the hip pattern retuned around the loss.',
    mods: [
      set('fireMode', 'auto'), scale('rpm', 0.62),
      mult('spread.hip', -0.20), mult('spread.hipMoving', -0.20),
      mult('recoil.climb30Deg', 0.15), mult('spread.bloomGain', 0.20),
    ],
    pro: 'Sustained automatic fire and the tightest hip cone the weapon has ever had.',
    con: 'The one-pull kill is gone and the time to kill nearly doubles.',
  },
  {
    id: 'my_rk9', weapon: 'rk9', slot: 'barrel', name: "RIFLEMAN'S SLEEVE",
    blurb: 'A long, shrouded barrel that makes the RK-9 pretend to be a carbine.',
    // Deliberately no damage change. A submachine gun that reaches must still lose
    // the trade against a rifle at rifle distance — this one buys range with
    // handling only, and its time to kill stays a submachine gun's.
    mods: [
      mult('damage.range', 0.35), mult('muzzleVelocity', 0.35), mult('penetration', 0.60),
      mult('ads.in', 0.20), mult('ergonomics.sprintOut', 0.30),
      mult('spread.hip', 0.35), mult('ergonomics.adsMoveScale', -0.15),
    ],
    pro: 'Holds its damage out to distances no other submachine gun sees.',
    con: 'Still kills slower than a rifle out there, and no longer wins up close.',
  },
  {
    id: 'my_dravec45', weapon: 'dravec45', slot: 'magazine', name: 'DRUMHEAD FEED',
    blurb: 'A sixty-round drum that hangs below the trigger guard like a saucepan.',
    mods: [
      mult('magSize', 0.67),
      mult('reload.scale', 0.50), mult('ergonomics.sprintOut', 0.35),
      mult('ergonomics.adsMoveScale', -0.15), mult('recoil.climb30Deg', 0.12),
    ],
    pro: 'Sixty rounds. Two or three engagements without touching the magazine.',
    con: 'When it does run dry you are out of the fight for a very long time.',
  },
  {
    id: 'my_ryden45k', weapon: 'ryden45k', slot: 'stock', name: 'SHOULDER OF THE OX',
    blurb: 'A heavy fixed stock and a weighted buffer bolted to the cheapest gun in the armoury.',
    mods: [
      mult('recoil.climb30Deg', -0.30), mult('spread.adsMoving', -0.35),
      mult('ergonomics.sprintOut', 0.45), mult('ergonomics.adsMoveScale', -0.25),
      mult('ads.in', 0.18),
    ],
    pro: 'The starter submachine gun becomes the steadiest one on the roster.',
    con: 'It also stops being a submachine gun in every way that mattered.',
  },
  {
    id: 'my_carbon57', weapon: 'carbon57', slot: 'magazine', name: 'CINDERFEED BOX',
    blurb: 'A hundred-round belt in a hard box. The barrel is the only thing rationing you.',
    mods: [
      mult('magSize', 1.00),
      set('overheat', { capacity: 1, perShot: 0.0115, coolRate: 0.30, coolDelay: 0.9, lockTime: 2.6 }),
      mult('reload.scale', 0.60), mult('ergonomics.sprintOut', 0.30),
      mult('ergonomics.adsMoveScale', -0.15),
    ],
    pro: 'A hundred rounds on tap from a submachine gun.',
    con: 'Eighty-seven of them and the barrel locks you out for two and a half seconds.',
  },

  /* -------------------------------- shotguns -------------------------------- */
  {
    id: 'my_m10breacher', weapon: 'm10breacher', slot: 'muzzle', name: 'THUNDERHEAD CHOKE',
    blurb: 'An extended full choke. Shouldered, it throws a fist rather than a hand.',
    // The one-shot *band* is untouched, here and everywhere: `CLASS_CLAMP.sg` pins a
    // shotgun's damage and its range to their base values. All this buys is a
    // pattern tight enough to actually land the shell that was always lethal.
    mods: [
      mult('pelletSpread', -0.48), set('pelletSpreadAdsScale', 0.70), mult('ads.in', -0.10),
      mult('spread.hip', 0.55), mult('spread.hipMoving', 0.55),
      mult('pumpTime', 0.30), mult('magSize', -0.25),
    ],
    pro: 'A shouldered pattern less than half its normal width.',
    con: 'Slower to work, six shells, and hopeless from the hip — where shotguns live.',
  },
  {
    id: 'my_echo12', weapon: 'echo12', slot: 'fireMod', name: 'CASCADE ACTION',
    blurb: 'A gas port drilled through the forend. The pump becomes a formality.',
    mods: [
      set('fireMode', 'semi'), scale('rpm', 1.60),
      mult('damage.mult', -0.20), mult('recoil.climb30Deg', 0.40),
      mult('spread.hip', 0.30), mult('magSize', -0.33),
    ],
    pro: 'Semi-automatic. Follow-up shells arrive in half the time.',
    con: 'Eight shells that each hit a fifth softer, out of a weapon that already needed two.',
  },
  {
    id: 'my_akita', weapon: 'akita', slot: 'barrel', name: 'SHOTLINE SLUG BARREL',
    blurb: 'A rifled barrel and a fixed rib. It stops being a shotgun and becomes a musket.',
    // `pellets -> 1` conserves the shell's total output before `damage.mult` is
    // applied — see `deriveEffective`. Ten pellets of 6 become one projectile of 60,
    // then 0.62 of that. It never one-shots, at any range, which is the point.
    mods: [
      set('pellets', 1), set('pelletSpread', 0),
      scale('damage.mult', 0.62), mult('damage.range', 1.40),
      mult('muzzleVelocity', 0.40), mult('penetration', 0.80),
      mult('recoil.climb30Deg', 0.35), mult('ads.in', 0.12), mult('spread.hip', 0.40),
    ],
    pro: 'A semi-automatic that puts a single accurate projectile out to rifle range.',
    con: 'Three hits to kill and not one pellet of forgiveness.',
  },

  /* ---------------------------------- LMGs ---------------------------------- */
  {
    id: 'my_xm325', weapon: 'xm325', slot: 'magazine', name: 'SUPPRESSION FEED',
    blurb: 'A hundred-round nutsack and a heavier barrel to survive it.',
    mods: [
      mult('magSize', 0.66), mult('recoil.climb30Deg', -0.20),
      // 0.0120, not 0.0100. At 0.0100 the bar reached 1.0 on round 100 of a
      // 100-round belt: the lockout fired on the last round or not at all, so the
      // con below was simply false — the belt gave out first, every time. 0.0120
      // locks at round 84 and leaves 16 in the box, which is the shape this is
      // supposed to have. Still gentler per round than the ordinary IRONFEED
      // (0.0125), because the blurb promises a heavier barrel and it should buy
      // something. Verified by measurement, not by reading the table.
      set('overheat', { capacity: 1, perShot: 0.0120, coolRate: 0.28, coolDelay: 1.0, lockTime: 2.8 }),
      mult('reload.scale', 0.55), mult('ergonomics.adsMoveScale', -0.30), mult('ads.in', 0.25),
    ],
    pro: 'A hundred flatter-shooting rounds, which is what suppression actually is.',
    con: 'The barrel gives out first, and reloading it takes most of a respawn.',
  },
  {
    id: 'my_mk78', weapon: 'mk78', slot: 'underbarrel', name: 'ANCHORPOINT BIPOD LOCK',
    blurb: 'A locking bipod and a monopod spike. Plant it and it stops being a rifle at all.',
    mods: [
      mult('recoil.climb30Deg', -0.45), mult('recoil.random', -0.40),
      mult('spread.adsMoving', -0.50),
      mult('ergonomics.adsMoveScale', -0.55), mult('ergonomics.sprintOut', 0.60),
      mult('ads.in', 0.30),
    ],
    pro: 'Seventy-five rounds that go almost exactly where the first one did.',
    con: 'You are furniture. Anything that flanks you has already won.',
  },

  /* ----------------------------- marksman rifles ---------------------------- */
  {
    id: 'my_warden308', weapon: 'warden308', slot: 'barrel', name: 'WARDEN LONGARM',
    blurb: 'A twenty-six inch heavy contour and a tuned gas block.',
    // No damage change on purpose. The Warden already one-shots a centre-mass hit;
    // its mastery buys reach and pays in rate and handling, so it can go where the
    // snipers live without also killing faster than they do once it gets there.
    mods: [
      mult('damage.range', 0.30), mult('muzzleVelocity', 0.30), mult('penetration', 0.50),
      scale('rpm', 0.70), mult('ads.in', 0.30), mult('ergonomics.sprintOut', 0.40),
      mult('aimSway.amp', 0.60), mult('ergonomics.adsMoveScale', -0.20),
    ],
    pro: 'Its one-shot chest hit holds out to 74 m instead of 57 m.',
    // Measured and deliberately capped there. The snipers reach 89 to 102 m and this
    // fires four times as fast as a bolt; letting it one-shot at sniper distance
    // would not be sharpening the marksman's role, it would be taking the sniper's.
    con: 'A third slower per round, and the sight will not sit still out there.',
  },
  {
    id: 'my_m8a1', weapon: 'm8a1', slot: 'fireMod', name: 'FULLHOUSE SEAR',
    blurb: 'The four-round counter deleted. Thirty-two rounds, one trigger pull.',
    mods: [
      set('fireMode', 'auto'), scale('rpm', 0.55),
      mult('damage.mult', -0.10), mult('recoil.climb30Deg', 0.30),
      mult('spread.bloomGain', 0.30), mult('ads.in', 0.08),
    ],
    pro: 'A thirty-two round automatic battle rifle.',
    con: 'The one-burst kill is gone and the replacement takes nearly twice as long.',
  },
  {
    id: 'my_m34novaline', weapon: 'm34novaline', slot: 'fireMod', name: 'NOVALINE GOVERNOR',
    blurb: 'A governor that permits one round per pull, loaded far past the proof mark.',
    // 50 -> 76. Still two body shots, but 114 to the head is one. A marksman rifle
    // that one-shots a head is a different promise from a sniper that one-shots a
    // chest, and it is paid for with a time-to-kill sixty per cent longer.
    mods: [
      set('fireMode', 'semi'), scale('rpm', 0.62), mult('damage.mult', 0.52),
      mult('magSize', -0.40), mult('recoil.climb30Deg', 0.45),
      mult('ads.in', 0.18), mult('aimSway.amp', 0.35),
    ],
    pro: 'One shot, one kill — to the head, at any range the curve reaches.',
    con: 'Twelve rounds, a slower kill on the body, and a sight that drifts.',
  },

  /* ------------------------------ sniper rifles ----------------------------- */
  {
    id: 'my_vsrecon', weapon: 'vsrecon', slot: 'fireMod', name: 'QUICKBOLT KIT',
    blurb: 'A short-throw bolt, an oversized handle and a light reduced-charge load.',
    mods: [
      mult('boltCycle', -0.28), mult('ads.in', -0.25), mult('ergonomics.sprintOut', -0.25),
      mult('damage.range', -0.38), mult('spread.adsMoving', 0.60),
      mult('recoil.climb30Deg', 0.30), add('flinch.resist', -0.25), mult('aimSway.amp', 0.55),
    ],
    pro: 'Scoped and cycling faster than any other bolt gun in the game.',
    con: 'The reduced charge costs it range, and it flinches like a man being shot at.',
  },
  {
    id: 'my_shadowsk', weapon: 'shadowsk', slot: 'barrel', name: 'NIGHTREACH BARREL',
    blurb: 'An integrally suppressed barrel with subsonic-tolerant rifling.',
    mods: [
      set('signature.minimap', false), set('signature.tracer', false),
      mult('signature.radius', -0.60), mult('damage.range', 0.15),
      mult('muzzleVelocity', -0.15), mult('ads.in', 0.20),
      mult('boltCycle', 0.15), mult('ergonomics.sprintOut', 0.25),
    ],
    pro: 'A sniper that never appears on the map, however long it stays in one place.',
    con: 'Slower rounds, a slower bolt, and slow to leave when it does get found.',
  },
  {
    id: 'my_xr3ion', weapon: 'xr3ion', slot: 'fireMod', name: 'ANTI-MATERIEL LOAD',
    blurb: 'A tungsten-cored round that treats a wall as an inconvenience.',
    mods: [
      mult('penetration', 0.90), mult('damage.mult', 0.12), mult('muzzleVelocity', 0.20),
      mult('boltCycle', 0.30), mult('magSize', -0.47),
      mult('ads.in', 0.28), mult('recoil.climb30Deg', 0.45), mult('reload.scale', 0.30),
    ],
    pro: 'Kills through cover that stops every other weapon in the game.',
    con: 'Eight rounds, the slowest bolt on the roster, and slower still to scope.',
  },
];

/** Mastery attachments, keyed by id. */
export const MASTERY = {};
/** Weapon id -> mastery attachment id. Exactly one entry per weapon. */
export const MASTERY_BY_WEAPON = {};

for (const e of M) {
  for (const m of e.mods) validateMod(m, e.id);
  const entry = {
    ...e,
    cost: 2,
    mastery: true,
    fit: { weapons: [e.weapon] },
    optic: null,
    // A mastery is behaviour as much as magnitude; its downsides are all numeric
    // anyway, but the flag keeps the audit honest if one ever stops being.
    structuralCon: true,
    unlockOrder: 999,
  };
  if (MASTERY[entry.id]) throw new Error(`[attachments] duplicate mastery id "${entry.id}"`);
  if (MASTERY_BY_WEAPON[e.weapon]) throw new Error(`[attachments] two masteries for "${e.weapon}"`);
  MASTERY[entry.id] = entry;
  MASTERY_BY_WEAPON[e.weapon] = entry.id;
}
